/**
 * LayerPromotionRail — calm rail surfaced at responsibility-layer
 * promotion moments (G2 stage release + G4 next-responsibility unlock).
 *
 * Owner-locked copy: the entire payload is read from the server (the
 * `engine/layer_promotion.py` module is the single source of truth) so
 * the wording cannot drift between G2 (StageConfirmationModal) and G4
 * (TrainingMode FocusCard) consumers.
 *
 * Doctrine
 * --------
 * Adding a new responsibility layer temporarily makes the drive feel
 * less smooth because the learner is holding previous routines AND
 * remembering the new layer at the same time. Without explicit calm
 * framing that can read as regression. This rail names the pattern
 * truthfully:
 *
 *   • Slow is control.
 *   • New layers are learned slower first.
 *   • You are building the next routine.
 *   • Control before speed.
 *   • Temporary slowing is expected and healthy.
 *
 * Visual contract (calm, slate-only, not a celebration popover):
 *   - Slate background, amber accent on the headline (matches the
 *     existing instructor doctrine rail).
 *   - No emojis, no animation, no popover.
 *   - Variant prop:
 *       "instructor" — dark slate, used inside StageConfirmationModal.
 *       "learner"    — light slate, used inside TrainingMode FocusCard.
 */
import { Shield } from "lucide-react";

export default function LayerPromotionRail({ note, variant = "instructor" }) {
  if (!note) return null;
  const { headline, lead, principles = [] } = note;
  if (!headline && !lead && principles.length === 0) return null;

  const isLearner = variant === "learner";
  const shell = isLearner
    ? "rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 text-slate-800 dark:text-slate-100"
    : "rounded-xl bg-slate-900 text-slate-100";
  const headlineColor = isLearner ? "text-amber-700" : "text-amber-400";
  const principleColor = isLearner
    ? "font-extrabold text-slate-900 dark:text-white"
    : "font-extrabold text-white";
  const supportingColor = isLearner
    ? "text-slate-600 dark:text-slate-300"
    : "text-slate-300";
  const leadColor = isLearner
    ? "text-slate-600 dark:text-slate-300"
    : "text-slate-300";

  return (
    <section
      data-testid="layer-promotion-rail"
      data-variant={variant}
      className={`${shell} p-4`}
    >
      <div className="flex items-center gap-2">
        <Shield className="w-4 h-4 text-amber-500" />
        <p
          className={`text-[10px] font-extrabold uppercase tracking-widest ${headlineColor}`}
          data-testid="layer-promotion-headline"
        >
          {headline}
        </p>
      </div>
      {lead && (
        <p
          className={`mt-2 text-[12.5px] leading-snug ${leadColor}`}
          data-testid="layer-promotion-lead"
        >
          {lead}
        </p>
      )}
      {principles.length > 0 && (
        <ul className="mt-2 space-y-1.5" data-testid="layer-promotion-principles">
          {principles.map((p, i) => (
            <li
              key={i}
              className="text-[12.5px]"
              data-testid={`layer-promotion-principle-${i}`}
            >
              <span className={principleColor}>{p.principle}</span>{" "}
              <span className={supportingColor}>{p.supporting}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
